/**
 * AffiliateBridge — 1.5s branded intermediate page before opening an affiliate link.
 *
 * URL: /go?url=<encoded>&token=<token>&block=<type>&item=<name>
 * Reinforces Escapafy brand, tracks the click, then redirects.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "./api";
import { LOGO_URL } from "../data/escapafy";

const HOST_HINTS = [
  { re: /booking\.com/i, name: "Booking.com" },
  { re: /iberia\./i, name: "Iberia" },
  { re: /qatarairways\./i, name: "Qatar Airways" },
  { re: /vueling\./i, name: "Vueling" },
  { re: /civitatis\./i, name: "Civitatis" },
  { re: /getyourguide\./i, name: "GetYourGuide" },
  { re: /viator\./i, name: "Viator" },
  { re: /expedia\./i, name: "Expedia" },
  { re: /skyscanner\./i, name: "Skyscanner" },
  { re: /airbnb\./i, name: "Airbnb" },
  { re: /rentalcars\./i, name: "Rentalcars" },
  { re: /trivago\./i, name: "trivago" },
];

function detectProvider(url) {
  try {
    const u = new URL(url);
    const hit = HOST_HINTS.find(({ re }) => re.test(u.hostname));
    if (hit) return hit.name;
    return u.hostname.replace(/^www\./, "");
  } catch {
    return "el proveedor";
  }
}

export default function AffiliateBridge() {
  const [params] = useSearchParams();
  const url = params.get("url") || "";
  const token = params.get("token") || "";
  const blockType = params.get("block") || "";
  const itemName = params.get("item") || "";
  const [countdown, setCountdown] = useState(2);
  const provider = detectProvider(url);

  useEffect(() => {
    if (!url) return;
    // Fire-and-forget click tracking
    if (token) {
      fetch(`${API}/public/proposals/${token}/click-track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ block_type: blockType, item_name: itemName, url }),
        keepalive: true,
      }).catch(() => {});
    }
    // Redirect after ~1.5s
    const t = setTimeout(() => { window.location.replace(url); }, 1500);
    const c = setInterval(() => setCountdown((n) => (n > 0 ? n - 1 : 0)), 750);
    return () => { clearTimeout(t); clearInterval(c); };
  }, [url, token, blockType, itemName]);

  if (!url) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-2xl text-[#0A192F]">Enlace no válido</h1>
          <p className="text-[#3A506B] mt-2 text-sm">No hemos podido encontrar el destino. Vuelve a la propuesta e inténtalo de nuevo.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A192F] text-[#FDFBF7] flex items-center justify-center p-6" data-testid="affiliate-bridge">
      <div className="max-w-lg text-center">
        <img src={LOGO_URL} alt="Escapafy" style={{ height: "56px", width: "auto", margin: "0 auto 32px", filter: "brightness(0) invert(1)" }} />
        <p className="text-[#E8C97A] tracking-[0.3em] text-xs uppercase mb-4 font-semibold">Te llevamos a</p>
        <h1 className="font-serif italic text-4xl lg:text-5xl mb-6">{provider}</h1>
        <p className="text-white/75 leading-relaxed">
          Gracias por confiar en <strong className="text-[#E8C97A]">Escapafy</strong> para organizar tu viaje.
          <br />
          Redirigiendo…
        </p>
        <div className="mt-10 flex items-center justify-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-[#E8C97A]"
              style={{ animation: `bridgeBlink 1.2s ease-in-out ${i * 0.2}s infinite` }}
            />
          ))}
        </div>
        <a
          href={url}
          className="mt-10 inline-block text-xs text-white/60 hover:text-[#E8C97A] underline underline-offset-4"
        >
          Continuar manualmente si no te redirige
        </a>
      </div>
      <style>{`@keyframes bridgeBlink { 0%,100% { opacity: 0.2 } 50% { opacity: 1 } }`}</style>
    </div>
  );
}
